import React from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";
import CTAButton from "./CTAButton";

// Mirrors the FAQPage JSON-LD in public/index.html.
// Source-of-truth for the visible FAQ. Keep these in sync.
const FAQS = [
  {
    q: "How much does restaurant menu photography cost in Melbourne?",
    a: "Volume-tier pricing: $30 per image for 15–20 images, $25 per image for 21–99 images, and $10 per image once you reach 100+ images (minimum spend $1,000). Most café and restaurant menu shoots land between $540 and $1,000.",
  },
  {
    q: "Do you come to my restaurant, or is the shoot in a studio?",
    a: "Every shoot is on-location at your venue. We bring professional lighting, surfaces and gear to your kitchen — no studio drop-offs, no shipping food across town.",
  },
  {
    q: "How long does a typical menu shoot take?",
    a: "A 15–25 dish shoot takes 3–4 hours on-site. A full 25–60 dish menu library is a single full day on-location (7–8 hours). Larger 60+ image catalogues split across two shorter days.",
  },
  {
    q: "What's the minimum booking size?",
    a: "15 images is the minimum. Below that, travel and setup costs make it uneconomical for both sides. Most cafés land at 18–25 images, most restaurants at 30–45.",
  },
  {
    q: "Do you shoot for Uber Eats, DoorDash and Menulog?",
    a: "Yes. Every menu shoot includes square plus landscape crops, consistent white-balance across dishes, and files named to match your menu items. Files are sized and ready to upload to Uber Eats, DoorDash, Menulog and your Google Business Profile.",
  },
  {
    q: "When do I get the photos back?",
    a: "Edited, web and print-ready files are delivered within 5–7 business days. Urgent 3-day turnarounds are available — mention the deadline during the call.",
  },
  {
    q: "Which Melbourne suburbs do you cover?",
    a: "All of Greater Melbourne. Strongest networks across the western suburbs (Footscray, Point Cook, Tarneit, Truganina, Werribee, Sunshine, Deer Park), plus regular work across the CBD, Carlton, Fitzroy, Brunswick, Richmond, South Yarra, St Kilda, Prahran, Epping, Craigieburn, Preston, Dandenong, Glen Waverley, Clayton and Springvale.",
  },
  {
    q: "What if I'm not happy with a photo?",
    a: "Every shoot includes one free re-edit per dish — no questions asked. If something fundamental needs a re-shoot, we organise that on the next available date.",
  },
  {
    q: "Can you also shoot the venue and interior?",
    a: "Yes — venue and ambience photography is a $450 add-on when combined with a menu shoot, covering front of house, fit-out and service shots. Standalone venue shoots are also available.",
  },
  {
    q: "Do I need to provide plates, surfaces or styling?",
    a: "No. We shoot on your real plates, in your real space — that's the whole point. Extra surfaces, props and lighting come with us.",
  },
];

export const FAQSection = () => (
  <section
    id="faq"
    data-testid="faq-section"
    className="bg-bone text-ink py-24 lg:py-36"
  >
    <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
      <div className="grid grid-cols-12 gap-8 lg:gap-16 mb-12 lg:mb-16 items-end">
        <div className="col-span-12 lg:col-span-7">
          <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-6 font-bold">
            Common questions · Pricing, process &amp; coverage
          </p>
          <h2
            className="font-display tracking-editorial leading-[0.95]"
            style={{ fontSize: "clamp(2.25rem, 5.5vw, 4.75rem)" }}
          >
            Everything operators
            <br />
            <span className="italic font-light text-ember">actually ask.</span>
          </h2>
        </div>
        <div className="col-span-12 lg:col-span-5 lg:pb-3">
          <p className="text-char text-base md:text-lg leading-relaxed max-w-md">
            If your question isn&apos;t answered below, the fastest path is to
            call directly — most quotes are confirmed in under five minutes
            on the phone.
          </p>
          <a href="tel:0494756111" className="inline-block mt-5">
            <CTAButton variant="secondary" size="md">Call 0494 756 111</CTAButton>
          </a>
        </div>
      </div>

      <Accordion
        type="single"
        collapsible
        className="border-t border-ink/15"
        data-testid="faq-accordion"
      >
        {FAQS.map((f, i) => (
          <AccordionItem
            key={i}
            value={`faq-${i}`}
            className="border-b border-ink/15"
            data-testid={`faq-item-${i}`}
          >
            <AccordionTrigger className="text-left py-6 md:py-8 hover:no-underline group">
              <div className="flex items-baseline gap-5 md:gap-8 pr-4 flex-1">
                <span className="font-sans font-bold text-sm md:text-base text-ember tracking-tight shrink-0">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="font-display text-xl md:text-2xl lg:text-3xl tracking-editorial leading-tight group-hover:text-ember transition-colors">
                  {f.q}
                </span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pb-8 pl-12 md:pl-16 pr-4 md:pr-12">
              <p className="text-base md:text-lg text-char leading-relaxed max-w-3xl">
                {f.a}
              </p>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>

      {/* Closing line */}
      <div className="mt-12 pt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <p className="text-sm text-char-mute max-w-xl">
          Got a question that&apos;s not here? Drop it in the enquiry form or
          ask on the call — happy to clarify anything before you commit.
        </p>
        <a href="#enquiry">
          <CTAButton variant="secondary" size="md" arrow>Start an enquiry</CTAButton>
        </a>
      </div>
    </div>
  </section>
);

export default FAQSection;
